import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, ProviderStatementItem } from '../api/admin';
import { incomingPaymentsApi, IncomingPayment } from '../api/payments';
import { providersApi, Provider } from '../api/providers';

function ReconciliationPage() {
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [items, setItems] = useState<ProviderStatementItem[]>([]);
  const [incoming, setIncoming] = useState<IncomingPayment[]>([]);
  const [providerId, setProviderId] = useState(() => searchParams.get('providerId') || '');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState<'ALL' | 'SWIFT' | 'SEPA'>('ALL');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const [statements, payments] = await Promise.all([
        adminApi.getProviderStatementItems(providerId || undefined, status || undefined),
        incomingPaymentsApi.list(500, providerId || undefined),
      ]);
      setItems(statements.data || []);
      setIncoming(payments.data || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to load statement items');
    } finally {
      setLoading(false);
    }
  }, [providerId, status]);

  useEffect(() => {
    void providersApi.getAll().then(response => setProviders(response || [])).catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const visibleIncoming = useMemo(
    () => incoming.filter((payment) => method === 'ALL' || (payment.paymentMethod || 'SWIFT') === method),
    [incoming, method],
  );

  const importStatement = async () => {
    if (!providerId || !file) {
      setMessage('Select provider and CSV file first.');
      return;
    }
    try {
      setLoading(true);
      const result = (await adminApi.importProviderStatement(providerId, file)).data;
      setMessage(`Imported: ${result.imported}. Duplicates skipped: ${result.duplicates}.${result.errors.length ? ` Errors: ${result.errors.join(' ')}` : ''}`);
      setFile(null);
      await loadItems();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to import statement');
    } finally {
      setLoading(false);
    }
  };

  const matchItem = async (item: ProviderStatementItem) => {
    const paymentId = window.prompt('Outgoing payment ID');
    if (!paymentId?.trim()) return;
    try {
      setLoading(true);
      await adminApi.matchProviderStatementItem(item.id, paymentId.trim());
      await loadItems();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to match statement item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Provider Reconciliation</h1>
      <p className="mt-1 text-sm text-gray-600">Import provider CSV statements and reconcile them with outgoing payments.</p>
      {message && <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">{message}</div>}
      <div className="mt-6 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <select value={providerId} onChange={e => setProviderId(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">Select provider</option>
          {providers.map(provider => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </select>
        <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <button onClick={() => void importStatement()} disabled={loading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Import CSV</button>
        <select value={status} onChange={e => setStatus(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="UNMATCHED">Unmatched</option>
          <option value="MATCHED">Matched</option>
        </select>
        <select value={method} onChange={e => setMethod(e.target.value as 'ALL' | 'SWIFT' | 'SEPA')} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="ALL">All methods</option>
          <option value="SWIFT">SWIFT</option>
          <option value="SEPA">SEPA</option>
        </select>
      </div>
      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Date</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Reference</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">IBAN</th><th className="px-4 py-3 text-right text-xs uppercase text-gray-500">Amount</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-gray-200">
            {items.map(item => <tr key={item.id}><td className="px-4 py-3 text-sm">{new Date(item.statementDate).toLocaleDateString()}</td><td className="px-4 py-3 text-sm">{item.providerReference || '—'}</td><td className="px-4 py-3 font-mono text-sm">{item.iban || '—'}</td><td className="px-4 py-3 text-right text-sm">{item.amount.toFixed(2)} {item.currencyId}</td><td className="px-4 py-3 text-sm">{item.matchStatus}</td><td className="px-4 py-3 text-right">{item.matchStatus === 'UNMATCHED' && <button onClick={() => void matchItem(item)} className="text-sm font-medium text-blue-700">Match</button>}</td></tr>)}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No statement items</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="mt-8 text-lg font-semibold text-gray-900">Incoming payments</h2>
      <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Date</th>
              <th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Method</th>
              <th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Received on IBAN</th>
              <th className="px-4 py-3 text-right text-xs uppercase text-gray-500">Amount</th>
              <th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visibleIncoming.map((payment) => (
              <tr key={payment.id}>
                <td className="px-4 py-3 text-sm">{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-sm">{payment.paymentMethod || 'SWIFT'}{payment.methodMismatch ? ' ⚠' : ''}</td>
                <td className="px-4 py-3 font-mono text-sm">{payment.receivedOnIban || '—'}</td>
                <td className="px-4 py-3 text-right text-sm">{payment.amount.toFixed(2)} {payment.currencyId}</td>
                <td className="px-4 py-3 text-sm">{payment.status}</td>
              </tr>
            ))}
            {!loading && visibleIncoming.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">No incoming payments</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReconciliationPage;
